import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@finn-auto/design-system/atoms/button';
import { AlertCircle, Loader2, RefreshCw, Search, Video } from 'lucide-react';
import { cn } from '../../../shared';
import { fetchInterviews, type InterviewRow } from '../services/interviewsApi';
import { InterviewRowBadges } from './interviewRowBadges';
import { InterviewCancelButton } from './InterviewCancelButton';
import { LogMoveButton, LogRescheduleProvider } from './logReschedule';
import { InterviewReplaceInterviewerButton } from './InterviewReplaceInterviewerButton';
import { DecisionLogPanel } from './DecisionLogPanel';
import { TimeSuggestionsPanel } from './TimeSuggestionsPanel';
import { SentEmailsPanel } from './SentEmailsPanel';
import { logAnalyticsEvent } from '../../../analytics/nativeAnalytics';
import { ScreeningLogPanel } from './ScreeningLogPanel';
import { FilterSelect, interviewFilterOptions, matchesAttributes } from './interviewFilters';
import { countStates, isMyInterview, ScopeToggle, type LogScope } from './logScope';
import { useAuth } from '../../../contexts/AuthContext';

const TZ = 'Europe/Berlin';
const whenFmt = new Intl.DateTimeFormat('en-GB', {
  timeZone: TZ,
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const personLabel = (email?: string | null) =>
  !email
    ? '—'
    : email
        .split('@')[0]
        .split('.')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');

type Filter = 'all' | 'upcoming' | 'cancelled' | 'rescheduled';

/**
 * What the Log is showing. The page grew from "booked interviews" into the
 * record of everything the board does, so the top-level choice is now the
 * TOPIC; the upcoming/cancelled chips below only ever made sense for
 * interviews and are now scoped to that section.
 */
type Topic = 'all' | 'interviews' | 'decisions' | 'emails' | 'screening';

const TOPICS: { id: Topic; label: string }[] = [
  { id: 'all', label: 'Everything' },
  { id: 'interviews', label: 'Interviews' },
  { id: 'decisions', label: 'Decisions' },
  { id: 'emails', label: 'Emails sent' },
  { id: 'screening', label: 'Screening log' },
];

/**
 * Every interview the booking links produced — live, moved and cancelled, with
 * the candidate's stated reason.
 *
 * Deliberately separate from Interview Links: that tab is the configuration, this
 * one is what actually happened. A cancelled interview keeps only its previous
 * time, so the row shows that rather than appearing to have none.
 */
export default function InterviewsView() {
  const { user } = useAuth();
  const me = user?.email ?? null;
  /** Mine by default — see logScope.tsx. 'everyone' is the holiday-cover case. */
  const [scope, setScope] = useState<LogScope>('mine');
  const [topic, setTopic] = useState<Topic>('all');
  /**
   * ALL by default, not "upcoming".
   *
   * "Upcoming" is server-computed from the interview's start, so a cancelled or
   * rescheduled interview is by definition not upcoming — and the page whose
   * whole job is to show what happened, cancellation reasons included, opened
   * with every one of those rows filtered out. Recruiters concluded the reasons
   * were not being recorded (Theresa, Sep 2026); they were, on the rows the
   * default filter hid.
   */
  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');
  /**
   * Three attribute filters beside the state dropdown (Sofia, Sep 10 2026): interviewer, position,
   * department. Each offers what the loaded rows actually contain — there is no separate roster to
   * read — and '' means "any". Interviewers are matched by email; the option shows the name.
   */
  const [interviewer, setInterviewer] = useState('');
  const [position, setPosition] = useState('');
  const [department, setDepartment] = useState('');

  const { data, isPending, error, refetch, isFetching } = useQuery({
    queryKey: ['interviews'],
    queryFn: fetchInterviews,
  });

  const options = useMemo(() => interviewFilterOptions(data?.interviews ?? []), [data]);

  const scopedRows = useMemo(
    () => (data?.interviews ?? []).filter((r) => scope === 'everyone' || isMyInterview(r, me)),
    [data, scope, me]
  );

  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();

    return scopedRows.filter((r) => {
      // `upcoming` comes from the server: calling Date.now() during render would
      // make this component impure.
      if (filter === 'upcoming' && !r.upcoming) return false;
      if (filter === 'cancelled' && r.status !== 'cancelled') return false;
      if (filter === 'rescheduled' && r.rescheduleCount === 0) return false;
      if (!matchesAttributes(r, { interviewer, position, department })) return false;
      if (!needle) return true;
      return [
        r.candidateName,
        r.candidateEmail,
        r.jobTitle,
        r.stageName,
        r.taOwner,
        ...r.interviewers,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(needle);
    });
  }, [scopedRows, filter, query, interviewer, position, department]);

  /**
   * Report that a search happened — never WHAT was searched for.
   *
   * The query on this page is very often a candidate's name, so the text itself can never reach a
   * sink under the PII rule. The useful signal survives without it: how often people fall back to
   * search (a list that needed searching is a list that did not surface the right thing) and
   * whether the search FOUND anything. Searches that return nothing are the actionable half.
   *
   * Debounced, because the input updates on every keystroke and one event per character would be
   * both useless and a way to reconstruct the very string that must not travel.
   */
  const lastReportedQuery = useRef('');
  useEffect(() => {
    const needle = query.trim();
    if (!needle || needle === lastReportedQuery.current) return;
    // Not while the list is still loading: `rows` is empty until the fetch lands, so reporting then
    // would record found:false for a search that in fact matched plenty.
    if (isPending) return;
    const timer = setTimeout(() => {
      lastReportedQuery.current = needle;
      void logAnalyticsEvent('log_searched', { result_count: rows.length, found: rows.length > 0 });
    }, 800);
    return () => clearTimeout(timer);
  }, [query, rows.length]);

  // The server's summary counts the whole team, so it cannot label a scoped list.
  const s = scope === 'mine' ? countStates(scopedRows) : data?.summary;
  const showInterviews = topic === 'all' || topic === 'interviews';
  const showDecisions = topic === 'all' || topic === 'decisions';
  const showEmails = topic === 'all' || topic === 'emails';
  const showScreening = topic === 'all' || topic === 'screening';

  // "All" first — it is the default, and a dropdown's first entry should be it.
  const chips: { id: Filter; label: string; count?: number }[] = [
    { id: 'all', label: 'All', count: s?.total },
    { id: 'upcoming', label: 'Upcoming', count: s?.upcoming },
    { id: 'rescheduled', label: 'Rescheduled', count: s?.rescheduled },
    { id: 'cancelled', label: 'Cancelled', count: s?.cancelled },
  ];

  return (
    <LogRescheduleProvider me={me} onChanged={() => void refetch()}>
    <div className="mx-auto max-w-5xl px-4 py-6">
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Log</h1>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Every interview booked through an interview link — including moved and cancelled ones,
            with the reason the candidate gave — plus every interview decision and candidate email
            the Action Board recorded, and a Screening Log of the rejection automation's activity
            (archived and sent counts, and any rejection that failed). Candidate data older than
            six months is deleted automatically.
          </p>
        </div>
        <Button size="sm" variant="ghost" onClick={() => void refetch()} disabled={isFetching}>
          <RefreshCw className={cn('mr-1.5 h-3.5 w-3.5', isFetching && 'animate-spin')} />
          Refresh
        </Button>
      </header>

      <ScopeToggle scope={scope} onChange={setScope} />

      <div className="mb-5 flex flex-wrap gap-1.5" role="tablist" aria-label="What to show">
        {TOPICS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={topic === t.id}
            onClick={() => {
              // Which of the three topics people actually read. The Log grew from "booked
              // interviews" into three things sharing a page; nothing until now said whether the
              // two newer ones earn their place.
              void logAnalyticsEvent('log_topic_changed', { topic: t.id });
              setTopic(t.id);
            }}
            className={cn(
              'rounded-full border px-3 py-1.5 text-sm font-medium',
              topic === t.id
                ? 'border-[#1A1A1A] bg-[#1A1A1A] text-white'
                : 'border-gray-300 bg-white text-gray-700 hover:border-gray-500'
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {showInterviews && (
      <>
      <TimeSuggestionsPanel mine={scope === 'mine' ? me : null} />
      {/* One dropdown, not four chips. Two rows of pills for what is really two
          axes (topic, then interview state) was the whole of the "simplify the
          menu" ask — the second row also only ever applied to one of the four
          topics, which is exactly what made it read as clutter. */}
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="min-w-[190px]">
          <span className="mb-1 block text-xs font-medium text-gray-500">Interviews</span>
          <select
            value={filter}
            onChange={(e) => {
              const next = e.target.value as Filter;
              void logAnalyticsEvent('log_filter_changed', { filter: next });
              setFilter(next);
            }}
            aria-label="Which interviews to show"
            className="h-9 w-full rounded-lg border border-gray-300 bg-white px-2 text-sm text-gray-700"
          >
            {chips.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
                {typeof c.count === 'number' ? ` (${c.count})` : ''}
              </option>
            ))}
          </select>
        </label>
        {/*
          Three attribute dropdowns, same shape as the state one. Options come from the loaded
          rows, each with its count, so an empty list can never be chosen.
        */}
        <FilterSelect
          label="Interviewer"
          value={interviewer}
          onChange={(v) => {
            void logAnalyticsEvent('log_filter_changed', { filter: 'interviewer' });
            // Naming someone means you want them, so stop scoping to yourself —
            // otherwise the two filters intersect to nothing and read as a bug.
            if (v) setScope('everyone');
            setInterviewer(v);
          }}
          options={options.interviewers.map(([email, n]) => [email, `${personLabel(email)} (${n})`])}
        />
        <FilterSelect
          label="Position"
          value={position}
          onChange={(v) => {
            void logAnalyticsEvent('log_filter_changed', { filter: 'position' });
            setPosition(v);
          }}
          options={options.positions.map(([title, n]) => [title, `${title} (${n})`])}
        />
        <FilterSelect
          label="Department"
          value={department}
          onChange={(v) => {
            void logAnalyticsEvent('log_filter_changed', { filter: 'department' });
            setDepartment(v);
          }}
          options={options.departments.map(([d, n]) => [d, `${d} (${n})`])}
        />
        {/*
          A plain <input>, not the design-system Input: that one hides its placeholder until focus
          (`placeholder:text-opacity-0`), which made this field read as an empty box with nothing to
          say what it was — the same deviation EmailBodyEditor's rewrite prompt documents. Fixed
          width rather than flex-1: a search field the width of the page looked like a missing panel.
        */}
        <label className="w-64">
          <span className="mb-1 block text-xs font-medium text-gray-500">Search</span>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Candidate, role, stage…"
              aria-label="Search interviews"
              className="h-9 w-full rounded-lg border border-gray-300 bg-white pl-8 pr-2 text-sm text-gray-700 placeholder:text-gray-400"
            />
          </div>
        </label>
      </div>

      {isPending ? (
        <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white p-8 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading interviews…
        </div>
      ) : error ? (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p>Could not load interviews.</p>
            <Button size="sm" variant="outline" className="mt-2" onClick={() => void refetch()}>
              Try again
            </Button>
          </div>
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 p-10 text-center">
          <p className="text-sm font-medium text-gray-700">No interviews</p>
          <p className="mt-1 text-xs text-gray-500">
            {data?.summary.total === 0
              ? 'Nothing has been booked through an interview link yet.'
              : 'Nothing matches this filter.'}
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          {rows.map((r, idx) => (
            <InterviewRowItem key={r.bookingRef} row={r} first={idx === 0} onChanged={() => void refetch()} />
          ))}
        </div>
      )}
      </>
      )}

      {showDecisions && <DecisionLogPanel mine={scope === 'mine' ? me : null} />}
      {showEmails && <SentEmailsPanel mine={scope === 'mine' ? me : null} />}
      {showScreening && <ScreeningLogPanel scoped={scope === 'mine'} />}
    </div>
    </LogRescheduleProvider>
  );
}

function InterviewRowItem({
  row,
  first,
  onChanged,
}: {
  row: InterviewRow;
  first: boolean;
  /** A move or a cancellation landed — the list refetches so the row shows real server state. */
  onChanged: () => void;
}) {
  const cancelled = row.status === 'cancelled';
  /**
   * The reason text, its label and its actor are resolved TOGETHER, from one action.
   *
   * They used to be picked apart: the text fell back from cancel to reschedule while the label
   * still said "Cancellation reason" and the actor was still read off the cancel fields. A booking
   * cancelled without a reason but moved earlier therefore rendered one sentence made of three
   * different events — the reschedule's words, attributed to whoever cancelled.
   */
  const shown = cancelled
    ? row.cancelReason
      ? { label: 'Cancellation reason', text: row.cancelReason, source: row.cancelSource, by: row.cancelledBy }
      : // Cancelled with no reason of its own: show the move that DID have one, labelled as a move.
        row.rescheduleReason
        ? { label: 'Reason for moving', text: row.rescheduleReason, source: row.rescheduleSource, by: row.rescheduledBy }
        : null
    : row.rescheduleReason
      ? { label: 'Reason for moving', text: row.rescheduleReason, source: row.rescheduleSource, by: row.rescheduledBy }
      : null;
  /**
   * Named only for FINN-side actions — the candidate's own page is the default, and saying so on
   * every row would be noise. `personLabel` rather than the raw address: the row prints names
   * everywhere else, and a second email in the line is hard to scan past.
   */
  const actor = shown?.source === 'finn' && shown.by ? personLabel(shown.by) : null;

  return (
    <div
      className={cn(
        'flex flex-wrap items-start gap-3 p-3.5',
        !first && 'border-t border-gray-100',
        cancelled && 'bg-gray-50'
      )}
    >
      <div className="min-w-[200px] flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={cn(
              'text-sm font-medium',
              cancelled ? 'text-gray-500 line-through' : 'text-gray-900'
            )}
          >
            {row.candidateName || row.candidateEmail || 'Unknown candidate'}
          </span>
          <InterviewRowBadges row={row} />
        </div>

        <p className="mt-0.5 text-xs text-gray-600">
          {row.start ? whenFmt.format(new Date(row.start)) : 'no time'}
          {row.minutes ? ` · ${row.minutes} min` : ''}
          {row.stageName ? ` · ${row.stageName}` : ''}
          {row.jobTitle ? ` · ${row.jobTitle}` : ''}
        </p>
        <p className="mt-0.5 text-xs text-gray-500">
          {row.interviewers.length ? row.interviewers.map(personLabel).join(', ') : 'no interviewer'}
          {row.candidateEmail ? ` · ${row.candidateEmail}` : ''}
        </p>

        {shown ? (
          <p className="mt-1 text-xs italic text-gray-600">
            {shown.label}: “{shown.text}”
            {/*
              WHO acted, because the two sides now have different vocabularies: a staff reason like
              "the interviewer can no longer make it" attributed to nobody reads as the candidate
              having said it. Only stated for FINN-side actions — the candidate's own page is the
              default and saying so on every row would be noise.
            */}
            {actor ? <span className="not-italic text-gray-500"> — by {actor}</span> : null}
          </p>
        ) : cancelled ? (
          <p className="mt-1 text-xs italic text-gray-400">No reason recorded</p>
        ) : null}
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-1.5">
        {row.meetLink && !cancelled ? (
          <a
            href={row.meetLink}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-800 hover:border-gray-500"
          >
            <Video className="h-3.5 w-3.5" />
            Meet
          </a>
        ) : null}
        {/*
          The "Manage" link is GONE on purpose (Aug 2026): it opened the candidate's own manage
          page — every employee got the candidate's credential, and staff cancels were recorded as
          the candidate's. This button is its replacement for the cancellations that have no
          inbound email (role closed, interviewer left); it runs on the non-credential bookingRef
          and the staff route. Never rebuild it on a token.
        */}
        {/*
          Lever did not follow this change. Shown rather than logged: the whole point of the sync is
          that Lever can be trusted, and the failure state is exactly when it cannot.
        */}
        {row.leverSync === 'failed' && (
          <span
            className="inline-flex items-center gap-1 rounded-lg bg-[#FCEEE2] px-2 py-1 text-xs font-medium text-[#854F0B]"
            title={row.leverSyncError || 'Lever was not updated'}
          >
            ⚠ Not in Lever
          </span>
        )}
        {!cancelled && row.upcoming ? (
          <>
            {/* Moving is the softer of the two, so it sits first — a recruiter reaching for
                "cancel" when they meant "move" costs the candidate their slot and the token. */}
            <LogMoveButton row={row} onChanged={onChanged} />
            {/*
              Replacing the interviewer is a 1:1-only action — a panel is agreed against the whole
              group. Shown only when the link is 1:1; `meetingType` is absent on an older proxy, so
              the button simply does not appear until the backend ships, never on a panel.
            */}
            {row.meetingType === '1:1' ? (
              <InterviewReplaceInterviewerButton
                bookingRef={row.bookingRef}
                candidateName={row.candidateName}
                onReplaced={onChanged}
              />
            ) : null}
            <InterviewCancelButton
              bookingRef={row.bookingRef}
              candidateName={row.candidateName}
              onCancelled={onChanged}
            />
          </>
        ) : null}
      </div>
    </div>
  );
}
